import React, { useEffect, useRef, useState } from "react";
import DashboardProfile from "./DashboardProfile";

import {
    Button,
    Input,
    Typography,
    Box,
    Paper,
    Divider,
} from "@mui/material";

import {
    Person,
    Badge,
    Email,
    Phone,
    CalendarMonth,
    LocationOn,
    AccountBalanceWallet,
    Lock,
    CloudUpload,
    Fingerprint,
    WorkspacePremium,
    Groups,
    AccessTime,
    School,
    Description,
    CurrencyRupee,
    Event,
    CheckCircle,
} from "@mui/icons-material";

import { userUpdate } from "../handler/LoginAndSingupHandler";
import UserLoader from "../Dashboard/UserLoader";
import { Link } from "react-router-dom";

const UserProfile = () => {
  const [file, setFile] = useState<File | null>(null);
  const [loader, setLoader] = useState(false);

  // ---------------------------------------------------------
  // DEFAULT USER PROFILE
  // ---------------------------------------------------------

  const userProfileData = {
    appToken: "",
    userInformationId: 5,
    fullName: "Robin Sharma",
    sponsorGrowniverseId: "string",
    growniverseId: "G10025",
    address: "string",
    country: "India",
    state: null,
    pincode: 0,
    userIdentityGuid:
      "ef035009-7095-4a70-8882-63a86f062681-5bd0bcdb-a6d8-47a5-9c81-708f75c0b95f",
    emailID: "string",
    phoneNumber: "string",
    emailConfirmation: true,
    packageId: 0,
    packageName: "",
    isAdmin: false,
    profileImage: "string",
    registrationDate: "",
    dateOfBirth: "",
    packageRequest: false,
  };

  const [userProfile, setUserProfile] = useState(userProfileData);

  // ---------------------------------------------------------
  // ACCOUNT DATA
  // ---------------------------------------------------------

  const [accountData, setAccountData] = useState({
    accountId: 1,
    unPaidBalance: 5840,
    totalIncome: 5840,
    todayIncome: 40,
    sevenDayIncome: 5840,
    monthlyIncome: 5840,
    transactions: [],
    accountStatements: [],
  });

  // ---------------------------------------------------------
  // INTERNSHIP DATA
  // ---------------------------------------------------------

  const [userInternship, setUserInternship] = useState<any>(null);

  const runOnce = useRef(false);

  // ---------------------------------------------------------
  // FILE CHANGE
  // ---------------------------------------------------------
  const normalizeDate = (date: Date): Date => {
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
  };
  const parseDate = (date: string): Date => {
    if (!date) {
      return new Date(NaN);
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] =
        date.split("-").map(Number);

      return new Date(
        year,
        month - 1,
        day
      );
    }

    return new Date(date);
  };
  const getRemainingDays = (endDate: string): number => {
    const end = normalizeDate(parseDate(endDate));

    const today = normalizeDate(new Date());

    if (isNaN(end.getTime())) {
      return 0;
    }

    const difference = end.getTime() - today.getTime();

    return Math.ceil(difference / (1000 * 60 * 60 * 24));
  };
  const remainingDays =
    userInternship
      ? getRemainingDays(
          userInternship.endDate
        )
      : 0;
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) return;

    const fileSizeInMB = selectedFile.size / (1024 * 1024);

    if (fileSizeInMB > 1) {
      alert("File size exceeds 1 MB. Please select a smaller file.");

      event.target.value = "";
      setFile(null);

      return;
    }

    setFile(selectedFile);
  };

  // ---------------------------------------------------------
  // UPDATE PROFILE
  // ---------------------------------------------------------

  const updateProfile = async () => {
    if (!file) {
      alert("Please select a profile image before uploading.");
      return;
    }

    setLoader(true);

    try {
      const formData = new FormData();

      formData.append(
        "UserInformationId",
        userProfile.userInformationId.toString(),
      );

      formData.append("ProfileImage", file);

      const response = await userUpdate(formData);

      if (response.statusCode === 200) {
        window.location.href = "/dashboard";
      } else {
        alert("Something went wrong. Please upload again.");
      }
    } catch (error) {
      console.error(error);

      alert("Something went wrong. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  // ---------------------------------------------------------
  // DATE FORMAT
  // ---------------------------------------------------------

  const DateTimeDisplay = (isoString: string, onlyDate: boolean) => {
    if (!isoString) return "—";

    const date = new Date(isoString);

    if (isNaN(date.getTime())) return "—";

    const day = date.getDate();

    const monthName = date.toLocaleString("en-US", {
      month: "long",
    });

    const year = date.getFullYear();

    if (onlyDate) {
      return `${day} ${monthName} ${year}`;
    }

    let hours = date.getHours();

    const isPM = hours >= 12;

    hours = hours % 12 || 12;

    const minutes = String(date.getMinutes()).padStart(2, "0");

    const amPm = isPM ? "PM" : "AM";

    return `${day} ${monthName} ${year} • ${hours}:${minutes} ${amPm}`;
  };

  // ---------------------------------------------------------
  // LOAD DATA
  // ---------------------------------------------------------

  useEffect(() => {
    if (runOnce.current) return;

    try {
      const storedProfile = localStorage.getItem("userProfile");

      const storedAccount = localStorage.getItem("account");

      const storedInternship = localStorage.getItem("userInternship");

      if (storedProfile) {
        setUserProfile(JSON.parse(storedProfile));
      }

      if (storedAccount) {
        setAccountData(JSON.parse(storedAccount));
      }

      if (storedInternship) {
        const parsedInternship = JSON.parse(storedInternship);

        setUserInternship(parsedInternship);
      }
    } catch (error) {
      console.error("Unable to load profile data", error);
    }

    runOnce.current = true;
  }, []);

  // ---------------------------------------------------------
  // FORMAT LABEL
  // ---------------------------------------------------------

  const formatLabel = (key: string) => {
    return key
      .replace(/([A-Z])/g, " $1")
      .replace(/[_-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  // ---------------------------------------------------------
  // CHECK WHETHER KEY IS AN ID
  // ---------------------------------------------------------

  const isIdField = (key: string) => {
    const normalizedKey = key.toLowerCase();

    return (
      normalizedKey === "id" ||
      normalizedKey.endsWith("id") ||
      normalizedKey.includes("guid") ||
      normalizedKey.includes("token") ||
      normalizedKey.includes("key")
    );
  };

  // ---------------------------------------------------------
  // FORMAT VALUE
  // ---------------------------------------------------------

  const formatValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }

    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }

    if (
      typeof value === "string" &&
      !isNaN(Date.parse(value)) &&
      (value.includes("-") || value.includes("/") || value.includes("T"))
    ) {
      return DateTimeDisplay(value, true);
    }

    if (Array.isArray(value)) {
      return value.map((item, index) => (
        <Box
          key={index}
          sx={{
            mb: index < value.length - 1 ? 1 : 0,
          }}
        >
          {typeof item === "object" ? renderObject(item) : String(item)}
        </Box>
      ));
    }

    if (typeof value === "object") {
      return renderObject(value);
    }

    return String(value);
  };

  // ---------------------------------------------------------
  // RENDER OBJECT
  // ---------------------------------------------------------

  const renderObject = (object: any): React.ReactNode => {
    if (!object || typeof object !== "object") {
      return String(object ?? "—");
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
        }}
      >
        {Object.entries(object)
          .filter(([key]) => !isIdField(key))
          .map(([key, value], index) => {
            if (value === null || value === undefined || value === "") {
              return null;
            }

            return (
              <Box
                key={`${key}-${index}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "9px",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    color: "#707a84",
                  }}
                >
                  {formatLabel(key)}
                </Typography>

                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#edf2f4",
                    wordBreak: "break-word",
                  }}
                >
                  {formatValue(value)}
                </Typography>
              </Box>
            );
          })}
      </Box>
    );
  };

  // ---------------------------------------------------------
  // GET INTERNSHIP DISPLAY DATA
  // ---------------------------------------------------------

  const getInternshipData = () => {
    if (!userInternship) {
      return [];
    }

    // If localStorage contains an array
    if (Array.isArray(userInternship)) {
      return userInternship;
    }

    // If API/localStorage wrapped the
    // internship inside a property
    if (Array.isArray(userInternship.data)) {
      return userInternship.data;
    }

    if (Array.isArray(userInternship.internships)) {
      return userInternship.internships;
    }

    // Single internship object
    return [userInternship];
  };

  const internshipList = getInternshipData();

  // ---------------------------------------------------------
  // INFO ITEM
  // ---------------------------------------------------------

  const InfoItem = ({
    icon,
    label,
    value,
    green = false,
  }: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    green?: boolean;
  }) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,

          padding: {
            xs: "13px",
            sm: "15px",
          },

          borderRadius: "13px",

          background: green
            ? "rgba(52, 211, 133, 0.035)"
            : "rgba(255, 0, 0, 0.12)",

          border: green
            ? "1px solid rgba(52,211,133,0.10)"
            : "1px solid rgba(255,255,255,0.07)",

          transition: "all .2s ease",

          "&:hover": {
            background: green
              ? "rgba(52,211,133,0.06)"
              : "rgba(255, 0, 0, 0.12)",

            borderColor: green
              ? "rgba(52,211,133,0.18)"
              : "rgba(255, 0, 0, 0.27)",

            transform: "translateY(-1px)",
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            minWidth: 40,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            borderRadius: "11px",

            background: green
              ? "rgba(52,211,133,0.08)"
              : "rgba(255,255,255,0.055)",

            border: green
              ? "1px solid rgba(52,211,133,0.12)"
              : "1px solid rgba(255,255,255,0.07)",

            color: green ? "#43d88f" : "#aab3bd",
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: "10px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.7px",
              color: "#78828d",
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              mt: "4px",

              fontSize: {
                xs: "13px",
                sm: "14px",
              },

              fontWeight: 600,

              color: "#eef2f4",

              overflow: "hidden",

              textOverflow: "ellipsis",

              whiteSpace: "nowrap",
            }}
          >
            {value || "—"}
          </Typography>
        </Box>
      </Box>
    );
  };

  // ---------------------------------------------------------
  // SECTION HEADER
  // ---------------------------------------------------------

  const SectionHeader = ({
    icon,
    title,
    subtitle,
  }: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
  }) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            minWidth: 42,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",

            borderRadius: "12px",

            background: "rgba(52,211,133,0.07)",

            border: "1px solid rgba(52,211,133,0.12)",

            color: "#43d88f",
          }}
        >
          {icon}
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: {
                xs: "16px",
                sm: "17px",
              },

              fontWeight: 700,

              color: "#f1f4f6",
            }}
          >
            {title}
          </Typography>

          <Typography
            sx={{
              fontSize: "11px",

              color: "#727d88",

              mt: "2px",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>
    );
  };

  // ---------------------------------------------------------
  // PAGE
  // ---------------------------------------------------------

  return (
    <Box
      sx={{
        minHeight: "100vh",

        background:
          "radial-gradient(circle at 20% 0%, rgba(38,211,129,.045), transparent 28%), #080c10",

        color: "#fff",

        position: "relative",

        overflow: "hidden",
      }}
    >
      {/* SUBTLE GRID */}

      <Box
        sx={{
          position: "absolute",

          inset: 0,

          pointerEvents: "none",

          opacity: 0.08,

          backgroundImage:
            "linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)",

          backgroundSize: "50px 50px",
        }}
      />

      {loader && <UserLoader />}

      {!loader && (
        <>
          {/* ------------------------------------------------ */}
          {/* PROFILE HEADER */}
          {/* ------------------------------------------------ */}

          <Box
            sx={{
              position: "relative",

              zIndex: 1,
            }}
          >
            <DashboardProfile
              userProfile={userProfile}
              totalIncome={accountData.totalIncome}
            />
          </Box>

          {/* ------------------------------------------------ */}
          {/* CONTENT */}
          {/* ------------------------------------------------ */}

          <Box
            sx={{
              position: "relative",

              zIndex: 2,

              maxWidth: "1150px",

              margin: "0 auto",

              px: {
                xs: 1.5,
                sm: 3,
                md: 4,
              },

              pb: 6,
            }}
          >
            {/* ------------------------------------------------ */}
            {/* PROFILE INFORMATION */}
            {/* ------------------------------------------------ */}

            <Paper
              elevation={0}
              sx={{
                mt: {
                  xs: 2,
                  md: 3,
                },

                p: {
                  xs: 2,
                  sm: 3,
                  md: 3.5,
                },

                borderRadius: "20px",

                background: "rgba(15,20,24,.96)",

                border: "1px solid rgba(255,255,255,.07)",

                boxShadow: "0 20px 50px rgba(0,0,0,.22)",
              }}
            >
              <SectionHeader
                icon={<Person />}
                title="Profile Information"
                subtitle="Personal and account details"
              />

              <Divider
                sx={{
                  mb: 2.5,
                  borderColor: "rgba(255,255,255,.06)",
                }}
              />

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    sm: "1fr 1fr",
                  },

                  gap: 1.3,
                }}
              >
                <InfoItem
                  icon={<Fingerprint />}
                  label="Growniverse ID"
                  value={userProfile.growniverseId}
                  green
                />

                <InfoItem
                  icon={<Groups />}
                  label="Rebate Code"
                  value={userProfile.sponsorGrowniverseId}
                  green
                />

                <InfoItem
                  icon={<WorkspacePremium />}
                  label="Package"
                  value={
                    userProfile.packageId != 0
                      ? userProfile.packageName
                      : "No Package"
                  }
                  green={userProfile.packageId !=0 }
                />

                <InfoItem
                  icon={<AccessTime />}
                  label="Registration"
                  value={DateTimeDisplay(userProfile.registrationDate, false)}
                  green
                />

                <InfoItem
                  icon={<Badge />}
                  label="Full Name"
                  value={userProfile.fullName}
                  green
                />

                <InfoItem
                  icon={<Email />}
                  label="Email Address"
                  value={userProfile.emailID}
                  green
                />

                <InfoItem
                  icon={<Phone />}
                  label="Phone Number"
                  value={userProfile.phoneNumber}
                  green
                />

                <InfoItem
                  icon={<CalendarMonth />}
                  label="Date Of Birth"
                  value={DateTimeDisplay(userProfile.dateOfBirth, true)}
                  green
                />

                <Box
                  sx={{
                    gridColumn: {
                      xs: "auto",

                      sm: "1 / -1",
                    },
                  }}
                >
                  <InfoItem
                    icon={<LocationOn />}
                    label="Address"
                    value={
                      <>
                        {userProfile.address}

                        {userProfile.state ? `, ${userProfile.state}` : ""}

                        {userProfile.pincode ? ` (${userProfile.pincode})` : ""}
                      </>
                    }
                  green
                  />
                </Box>
              </Box>
            </Paper>

            {/* ------------------------------------------------ */}
            {/* ACCOUNT OVERVIEW */}
            {/* ------------------------------------------------ */}

            <Paper
              elevation={0}
              sx={{
                mt: 2,

                p: {
                  xs: 2,
                  sm: 3,
                  md: 3.5,
                },

                borderRadius: "20px",

                background:
                  "linear-gradient(145deg, rgba(14,28,22,.97), rgba(10,19,15,.97))",

                border: "1px solid rgba(52,211,133,.12)",

                boxShadow: "0 20px 50px rgba(0,0,0,.20)",
              }}
            >
              <SectionHeader
                icon={<AccountBalanceWallet />}
                title="Account Overview"
                subtitle="Income and balance summary"
              />

              <Divider
                sx={{
                  mb: 2.5,

                  borderColor: "rgba(52,211,133,.08)",
                }}
              />

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    sm: "1fr 1fr",

                    md: "repeat(3, 1fr)",
                  },

                  gap: 1.3,
                }}
              >
                <InfoItem
                  icon={<AccountBalanceWallet />}
                  label="Total Income"
                  value={`₹${accountData.totalIncome || 0}`}
                  green
                />

                <InfoItem
                  icon={<AccountBalanceWallet />}
                  label="Available Balance"
                  value={`₹${accountData.unPaidBalance || 0}`}
                  green
                />

                <InfoItem
                  icon={<AccessTime />}
                  label="Today's Income"
                  value={`₹${accountData.todayIncome || 0}`}
                  green
                />

                <InfoItem
                  icon={<AccountBalanceWallet />}
                  label="7 Day Income"
                  value={`₹${accountData.sevenDayIncome || 0}`}
                  green
                />

                <InfoItem
                  icon={<AccountBalanceWallet />}
                  label="Monthly Income"
                  value={`₹${accountData.monthlyIncome || 0}`}
                  green
                />
              </Box>
            </Paper>

            {/* ------------------------------------------------ */}
            {/* MY INTERNSHIP */}
            {/* ------------------------------------------------ */}

            {userInternship && (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,

                  p: {
                    xs: 2,
                    sm: 3,
                    md: 3.5,
                  },

                  borderRadius: "20px",

                  background:
                    "linear-gradient(145deg, rgba(14,28,22,.97), rgba(10,19,15,.97))",

                  border: "1px solid rgba(52,211,133,.12)",

                  boxShadow: "0 20px 50px rgba(0,0,0,.20)",
                }}
              >
                <SectionHeader
                  icon={<School />}
                  title="My Internship"
                  subtitle="Internship details and selected courses"
                />

                <Divider
                  sx={{
                    mb: 2.5,

                    borderColor: "rgba(52,211,133,.08)",
                  }}
                />

                {internshipList.map(
                  (internship: any, internshipIndex: number) => (
                    <Box
                      key={internshipIndex}
                      sx={{
                        mb: internshipIndex < internshipList.length - 1 ? 2 : 0,
                      }}
                    >
                      {/* INTERNSHIP CARD */}

                      <Box
                        sx={{
                          border: "1px solid rgba(52,211,133,.10)",

                          borderRadius: "15px",

                          background: "rgba(52,211,133,.025)",

                          overflow: "hidden",
                        }}
                      >
                        {/* CARD HEADER */}

                        <Box
                          sx={{
                            px: {
                              xs: 2,
                              sm: 2.5,
                            },

                            py: 1.8,

                            display: "flex",

                            alignItems: "center",

                            gap: 1.5,

                            borderBottom: "1px solid rgba(52,211,133,.08)",

                            background: "rgba(52,211,133,.035)",
                          }}
                        >
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              minWidth: 42,

                              display: "flex",

                              alignItems: "center",

                              justifyContent: "center",

                              borderRadius: "11px",

                              color: "#43d88f",

                              background: "rgba(52,211,133,.08)",

                              border: "1px solid rgba(52,211,133,.12)",
                            }}
                          >
                            <WorkspacePremium />
                          </Box>

                          <Box
                            sx={{
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "15px",

                                fontWeight: 700,

                                color: "#edf2f4",

                                wordBreak: "break-word",
                              }}
                            >
                              {internship.internshipName ||
                                internship.name ||
                                internship.title ||
                                "Internship"}
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: "10px",

                                color: "#74808a",

                                mt: "3px",
                              }}
                            >
                              Internship Program
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",

                              alignItems: "center",

                              gap: 0.5,

                              px: 1.2,

                              py: ".4rem",

                              borderRadius: "20px",

                              color: "#43d88f",

                              background: "rgba(52,211,133,.07)",

                              border: "1px solid rgba(52,211,133,.12)",

                              fontSize: "9px",

                              fontWeight: 800,

                              letterSpacing: ".5px",
                            }}
                          >
                            <CheckCircle
                              sx={{
                                fontSize: 13,
                              }}
                            />
                            ACTIVE
                          </Box>
                        </Box>

                        {/* INTERNSHIP DETAILS */}

                        <Box
                          sx={{
                            p: {
                              xs: 2,
                              sm: 2.5,
                            },

                            display: "grid",

                            gridTemplateColumns: {
                              xs: "1fr",

                              sm: "1fr 1fr",
                            },

                            gap: 1.3,
                          }}
                        >
                          {Object.entries(internship)
                            .filter(([key]) => !isIdField(key))
                            .map(([key, value], index) => {
                              if (
                                value === null ||
                                value === undefined ||
                                value === ""
                              ) {
                                return null;
                              }

                              /*
                               * Selected course arrays / nested
                               * objects are displayed separately
                               * below.
                               */

                              if (
                                Array.isArray(value) ||
                                (typeof value === "object" && value !== null)
                              ) {
                                return null;
                              }

                              const normalizedKey = key.toLowerCase();

                              let icon: React.ReactNode = <Description />;

                              if (normalizedKey.includes("duration")) {
                                icon = <AccessTime />;
                              } else if (
                                normalizedKey.includes("price") ||
                                normalizedKey.includes("amount")
                              ) {
                                icon = <CurrencyRupee />;
                              } else if (
                                normalizedKey.includes("date") ||
                                normalizedKey.includes("start") ||
                                normalizedKey.includes("end")
                              ) {
                                icon = <Event />;
                              } else if (
                                normalizedKey.includes("description")
                              ) {
                                icon = <Description />;
                              }

                              return (
                                <InfoItem
                                  key={`${key}-${index}`}
                                  icon={icon}
                                  label={formatLabel(key)}
                                  value={formatValue(value)}
                                  green
                                />
                              );
                            })}
                          <InfoItem
                            label={"Remaning"}
                            value={remainingDays}
                            icon={<AccessTime />}
                          />
                        </Box>

                        {/* NESTED / COURSE DATA */}

                        {Object.entries(internship)
                          .filter(
                            ([key, value]) =>
                              !isIdField(key) &&
                              (Array.isArray(value) ||
                                (typeof value === "object" && value !== null)),
                          )
                          .map(([key, value], index) => {
                            if (!value) {
                              return null;
                            }

                            return (
                              <Box
                                key={`${key}-nested-${index}`}
                                sx={{
                                  mx: {
                                    xs: 2,
                                    sm: 2.5,
                                  },

                                  mb: 2,

                                  p: {
                                    xs: 1.5,
                                    sm: 2,
                                  },

                                  borderRadius: "13px",

                                  background: "rgba(255,255,255,.025)",

                                  border: "1px solid rgba(255,255,255,.06)",
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "10px",

                                    fontWeight: 800,

                                    textTransform: "uppercase",

                                    letterSpacing: ".7px",

                                    color: "#43d88f",

                                    mb: 1.2,
                                  }}
                                >
                                  {formatLabel(key)}
                                </Typography>

                                {Array.isArray(value) ? (
                                  <Box
                                    sx={{
                                      display: "grid",

                                      gridTemplateColumns: {
                                        xs: "1fr",

                                        sm: "1fr 1fr",
                                      },

                                      gap: 1,
                                    }}
                                  >
                                    {value.map(
                                      (item: any, itemIndex: number) => (
                                        <Box
                                          key={itemIndex}
                                          sx={{
                                            p: 1.5,

                                            borderRadius: "11px",

                                            background: "rgba(52,211,133,.035)",

                                            border:
                                              "1px solid rgba(52,211,133,.07)",
                                          }}
                                        >
                                          {typeof item === "object" ? (
                                            renderObject(item)
                                          ) : (
                                            <Typography
                                              sx={{
                                                fontSize: "12px",

                                                color: "#e8edef",

                                                fontWeight: 600,
                                              }}
                                            >
                                              {item}
                                            </Typography>
                                          )}
                                        </Box>
                                      ),
                                    )}
                                  </Box>
                                ) : (
                                  renderObject(value)
                                )}
                              </Box>
                            );
                          })}
                      </Box>
                    </Box>
                  ),
                )}
              </Paper>
            )}

            {/* ------------------------------------------------ */}
            {/* AGREEMENT */}
            {/* ------------------------------------------------ */}

            <Paper
              elevation={0}
              sx={{
                mt: 2,

                p: {
                  xs: 2,
                  sm: 2.5,
                },

                borderRadius: "18px",

                background: "rgba(255,255,255,.025)",

                border: "1px solid rgba(255,255,255,.06)",
              }}
            >
              <Box
                sx={{
                  display: "flex",

                  alignItems: "center",

                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    minWidth: 40,

                    display: "flex",

                    alignItems: "center",

                    justifyContent: "center",

                    borderRadius: "11px",

                    color: "#43d88f",

                    background: "rgba(52,211,133,.07)",

                    border: "1px solid rgba(52,211,133,.11)",
                  }}
                >
                  <Badge />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",

                      fontWeight: 700,

                      color: "#edf1f3",
                    }}
                  >
                    Account Agreement
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "11px",

                      color: "#727c86",

                      mt: "2px",
                    }}
                  >
                    Your membership agreement is active
                  </Typography>
                </Box>

                <Box
                  sx={{
                    px: 1.4,

                    py: ".45rem",

                    borderRadius: "20px",

                    color: "#43d88f",

                    fontSize: "9px",

                    fontWeight: 800,

                    letterSpacing: ".6px",

                    background: "rgba(52,211,133,.07)",

                    border: "1px solid rgba(52,211,133,.12)",
                  }}
                >
                  ACTIVE
                </Box>
              </Box>
            </Paper>

            {/* ------------------------------------------------ */}
            {/* PROFILE IMAGE */}
            {/* ------------------------------------------------ */}

            <Paper
              elevation={0}
              sx={{
                mt: 2,

                p: {
                  xs: 2,
                  sm: 3,
                },

                borderRadius: "20px",

                background: "rgba(15,20,24,.96)",

                border: "1px solid rgba(255,255,255,.07)",
              }}
            >
              <SectionHeader
                icon={<CloudUpload />}
                title="Profile Image"
                subtitle="Update your profile picture"
              />

              <Box
                sx={{
                  border: "1px dashed rgba(52,211,133,.18)",

                  borderRadius: "15px",

                  p: {
                    xs: 2.5,
                    sm: 3,
                  },

                  textAlign: "center",

                  background: "rgba(52,211,133,.018)",
                }}
              >
                <CloudUpload
                  sx={{
                    fontSize: 34,

                    color: "#43d88f",

                    mb: 1,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: "14px",

                    fontWeight: 600,

                    color: "#e6ebee",
                  }}
                >
                  Select profile image
                </Typography>

                <Typography
                  sx={{
                    fontSize: "10px",

                    color: "#707a84",

                    mt: ".5rem",

                    mb: 2,
                  }}
                >
                  PNG or JPG • Maximum 1 MB
                </Typography>

                <Input
                  type="file"
                  name="imageFileData"
                  onChange={handleFileChange}
                  inputProps={{
                    accept: "image/png,image/jpeg",
                  }}
                  sx={{
                    color: "#aeb7bf",

                    mb: 1.5,
                  }}
                />

                {file && (
                  <Typography
                    sx={{
                      fontSize: "11px",

                      color: "#43d88f",

                      mb: 1.5,
                    }}
                  >
                    ✓ {file.name}
                  </Typography>
                )}

                <Button
                  onClick={updateProfile}
                  startIcon={<CloudUpload />}
                  variant="contained"
                  sx={{
                    mt: ".5rem",

                    px: 2.8,

                    py: 1.1,

                    borderRadius: "11px",

                    textTransform: "none",

                    fontWeight: 700,

                    fontSize: "12px",

                    background: "#32c77d",

                    color: "#06120c",

                    boxShadow: "none",

                    "&:hover": {
                      background: "#43d88f",

                      boxShadow: "0 6px 20px rgba(52,211,133,.12)",
                    },
                  }}
                >
                  Upload Image
                </Button>
              </Box>
            </Paper>

            {/* ------------------------------------------------ */}
            {/* SECURITY */}
            {/* ------------------------------------------------ */}

            <Paper
              elevation={0}
              sx={{
                mt: 2,

                p: {
                  xs: 2,
                  sm: 2.5,
                },

                borderRadius: "18px",

                background: "rgba(15,20,24,.96)",

                border: "1px solid rgba(255,255,255,.07)",
              }}
            >
              <Box
                sx={{
                  display: "flex",

                  alignItems: "center",

                  justifyContent: "space-between",

                  gap: 2,

                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: "flex",

                    alignItems: "center",

                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,

                      display: "flex",

                      alignItems: "center",

                      justifyContent: "center",

                      borderRadius: "11px",

                      color: "#43d88f",

                      background: "rgba(52,211,133,.07)",

                      border: "1px solid rgba(52,211,133,.11)",
                    }}
                  >
                    <Lock />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: "14px",

                        fontWeight: 700,

                        color: "#edf1f3",
                      }}
                    >
                      Account Security
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "10px",

                        color: "#707a84",

                        mt: ".3rem",
                      }}
                    >
                      Manage your password
                    </Typography>
                  </Box>
                </Box>

                <Button
                  component={Link}
                  to="/changepassword"
                  startIcon={<Lock />}
                  sx={{
                    borderRadius: "10px",

                    px: 2.2,

                    py: ".8rem",

                    color: "#43d88f",

                    border: "1px solid rgba(52,211,133,.15)",

                    background: "rgba(52,211,133,.035)",

                    textTransform: "none",

                    fontSize: "12px",

                    fontWeight: 700,

                    "&:hover": {
                      background: "rgba(52,211,133,.07)",

                      borderColor: "rgba(52,211,133,.25)",
                    },
                  }}
                >
                  Change Password
                </Button>
              </Box>
            </Paper>

            {/* ------------------------------------------------ */}
            {/* FOOTER */}
            {/* ------------------------------------------------ */}

            <Box
              sx={{
                textAlign: "center",

                py: 4,
              }}
            >
              <Typography
                sx={{
                  fontSize: "9px",

                  letterSpacing: "1.5px",

                  color: "#454e57",

                  textTransform: "uppercase",
                }}
              >
                GROWNIVERSE • ACCOUNT CENTER
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};;

export default UserProfile;